import React, { useCallback, useMemo } from 'react';
import { AutocompleteDropdown, Box, Field, FieldTypes } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { OptionKey } from '@/common/types';
import { IReturnMailOcrDetailsOriginalMailingTypeEnum } from '@/dtos/models/IReturnMailOcrDetails';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

const MAILING_TYPE_OPTIONS = [
  { key: IReturnMailOcrDetailsOriginalMailingTypeEnum.WELCOME_LETTER, value: IReturnMailOcrDetailsOriginalMailingTypeEnum.WELCOME_LETTER, title: 'HSA Welcome Letter' },
  { key: IReturnMailOcrDetailsOriginalMailingTypeEnum.ORPHAN_LETTER, value: IReturnMailOcrDetailsOriginalMailingTypeEnum.ORPHAN_LETTER, title: 'Orphan Letter' },
  { key: IReturnMailOcrDetailsOriginalMailingTypeEnum.STATEMENT, value: IReturnMailOcrDetailsOriginalMailingTypeEnum.STATEMENT, title: 'Statement' },
  { key: IReturnMailOcrDetailsOriginalMailingTypeEnum.TAX_FORM, value: IReturnMailOcrDetailsOriginalMailingTypeEnum.TAX_FORM, title: 'Tax Form' },
];

const getMailingTypeTitle = (value?: string) =>
  MAILING_TYPE_OPTIONS.find((option) => option.value === value)?.title || value || EMPTY_FIELD_VALUE;

const MailingTypeDropdown: React.FC = () => {
  const { originalLockbox, editableLockbox, setEditableLockbox } = useLockboxDetailsStore();
  const ocrMailingType = originalLockbox?.details?.originalMailingType
    || originalLockbox?.ocr?.details?.originalMailingType;
  const currentValue = editableLockbox?.mailingType || ocrMailingType || '';

  const handleChange = useCallback((value: OptionKey) => {
    setEditableLockbox({ mailingType: value as string });
  }, [setEditableLockbox]);

  return (
    <Box width="250px">
      <AutocompleteDropdown
        testId="lockbox-mailing-type"
        name="mailingType"
        value={currentValue}
        defaultValue={currentValue}
        onChange={handleChange}
        options={MAILING_TYPE_OPTIONS}
        placeholder="Mailing type"
        disableFiltering
        isSmall
        isBold
      />
    </Box>
  );
};

const useLockboxReturnMailFields = (isEditMode?: boolean) => {
  const { originalLockbox } = useLockboxDetailsStore();

  const mailingTypeValue = originalLockbox?.details?.originalMailingType
    || originalLockbox?.ocr?.details?.originalMailingType;

  const lockboxReturnMailFields: Field[] = useMemo(() => [
    {
      type: FieldTypes.Text,
      name: 'employeeName',
      label: 'Employee name',
      value: originalLockbox?.ocr?.employeeName || EMPTY_FIELD_VALUE,
    },
    {
      type: FieldTypes.Text,
      name: 'address',
      label: 'Address',
      value: originalLockbox?.ocr?.details?.address
        ? [
            originalLockbox.ocr.details.address.line1,
            originalLockbox.ocr.details.address.city,
            originalLockbox.ocr.details.address.state,
            originalLockbox.ocr.details.address.zipcode,
          ].filter(Boolean).join(', ')
        : EMPTY_FIELD_VALUE,
    },
    isEditMode
      ? {
          type: FieldTypes.Node,
          name: 'mailingType',
          label: 'Mailing type',
          value: '',
          node: <MailingTypeDropdown />,
        }
      : {
          type: FieldTypes.Text,
          name: 'mailingType',
          label: 'Mailing type',
          value: getMailingTypeTitle(mailingTypeValue),
        },
  ], [originalLockbox, isEditMode, mailingTypeValue]);

  return {
    lockboxReturnMailFields,
  };
};

export default useLockboxReturnMailFields;
